import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import { Receipt, CheckCircle, Print, Close } from '@mui/icons-material';
import invoiceService from '../services/invoiceService';
import useInvoice from '../hooks/useInvoice';

const InvoiceModal = ({ open, onClose, sale, onSuccess }) => {
  const [tiposComprobante, setTiposComprobante] = useState({});
  const [tiposDocumento, setTiposDocumento] = useState({});
  const [formData, setFormData] = useState({
    tipo_comprobante: 11, // Factura C por defecto
    documento_tipo: 99,   // Consumidor Final por defecto
    documento_nro: 0
  });
  const [invoice, setInvoice] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const { loading, error, generateInvoice } = useInvoice();

  useEffect(() => {
    if (open) {
      loadTipos();
      setInvoice(null);
      setFormData({
        tipo_comprobante: 11,
        documento_tipo: 99,
        documento_nro: 0
      });
    }
  }, [open]);

  const loadTipos = async () => {
    setLocalLoading(true);
    try {
      const [comprobantes, documentos] = await Promise.all([
        invoiceService.getTiposComprobante(),
        invoiceService.getTiposDocumento()
      ]);
      if (comprobantes.success) setTiposComprobante(comprobantes.data);
      if (documentos.success) setTiposDocumento(documentos.data);
    } catch (err) {
      console.error('Error al cargar tipos:', err);
    }
    setLocalLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['tipo_comprobante', 'documento_tipo', 'documento_nro'];
    setFormData(prev => ({
      ...prev,
      [name]: numericFields.includes(name) ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await generateInvoice(sale.id, formData);
    if (result.success) {
      setInvoice(result.data);
      if (onSuccess) onSuccess(result.data);
    }
  };

  const handleClose = () => {
    setInvoice(null);
    onClose();
  };

  const handlePrint = () => {
    window.print();
  };

  if (!sale) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box className="d-flex align-center gap-sm">
          <Receipt color="primary" />
          <Typography variant="h6" component="span">
            Generar Factura Electrónica
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Información de la venta */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.lighter', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Venta:</strong> {sale.saleNumber}
          </Typography>
          <Typography variant="h6" color="primary.main">
            <strong>Total:</strong> ${parseFloat(sale.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </Typography>
        </Paper>

        {localLoading ? (
          <Box className="d-flex justify-center py-lg">
            <CircularProgress />
          </Box>
        ) : !invoice ? (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              select
              label="Tipo de Comprobante"
              name="tipo_comprobante"
              value={formData.tipo_comprobante}
              onChange={handleChange}
              required
              margin="normal"
            >
              {Object.entries(tiposComprobante).map(([codigo, nombre]) => (
                <MenuItem key={codigo} value={parseInt(codigo)}>
                  {nombre}
                </MenuItem>
              ))}
            </TextField>

            <Alert severity="info" sx={{ my: 2 }}>
              <Typography variant="caption">
                <strong>Factura C:</strong> Para Monotributo o Consumidor Final<br />
                <strong>Factura B:</strong> Para Responsable Inscripto
              </Typography>
            </Alert>

            <TextField
              fullWidth
              select
              label="Tipo de Documento"
              name="documento_tipo"
              value={formData.documento_tipo}
              onChange={handleChange}
              required
              margin="normal"
            >
              {Object.entries(tiposDocumento).map(([codigo, nombre]) => (
                <MenuItem key={codigo} value={parseInt(codigo)}>
                  {nombre}
                </MenuItem>
              ))}
            </TextField>

            {formData.documento_tipo !== 99 && (
              <TextField
                fullWidth
                type="number"
                label="Número de Documento"
                name="documento_nro"
                value={formData.documento_nro}
                onChange={handleChange}
                placeholder="Ej: 12345678"
                required
                margin="normal"
                helperText="Ingrese el número de documento sin puntos ni guiones"
              />
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        ) : (
          <Box>
            <Box className="d-flex justify-center mb-md">
              <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
            </Box>
            
            <Typography variant="h6" align="center" className="mb-md" color="success.main">
              ¡Factura Generada Exitosamente!
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ '& > div': { mb: 1.5 } }}>
              <Box className="d-flex justify-between">
                <Typography variant="body2" color="text.secondary">Tipo:</Typography>
                <Typography variant="body1" fontWeight="600">{invoice.invoice_type}</Typography>
              </Box>
              
              <Box className="d-flex justify-between">
                <Typography variant="body2" color="text.secondary">Número:</Typography>
                <Typography variant="body1" fontWeight="600">{invoice.invoice_number}</Typography>
              </Box>
              
              <Divider />
              
              <Box className="d-flex justify-between">
                <Typography variant="body2" color="text.secondary">CAE:</Typography>
                <Typography 
                  variant="body1" 
                  fontWeight="600"
                  sx={{ 
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1
                  }}
                >
                  {invoice.cae}
                </Typography>
              </Box>
              
              <Box className="d-flex justify-between">
                <Typography variant="body2" color="text.secondary">Vencimiento CAE:</Typography>
                <Typography variant="body1" fontWeight="600">
                  {new Date(invoice.cae_expiration).toLocaleDateString('es-AR')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!invoice ? (
          <>
            <Button 
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || localLoading}
              startIcon={loading ? <CircularProgress size={20} /> : <Receipt />}
            >
              {loading ? 'Generando...' : 'Generar Factura'}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handlePrint}
              startIcon={<Print />}
              variant="outlined"
            >
              Imprimir
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              startIcon={<Close />}
            >
              Cerrar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;

